import React, { useEffect, useState } from 'react'
import { NavLink, Outlet, useNavigate, useLocation } from 'react-router-dom'
import { useAuth } from '../context/AuthContext'
import { tradingApi, TradingStatus } from '../services/api'

const MENU: Array<{ to: string; label: string; icon: string; end?: boolean }> = [
  { to: '/', label: 'داشبورد', icon: '📊', end: true },
  { to: '/trades', label: 'خرید و فروش', icon: '🔄' },
  { to: '/ledger', label: 'تراز', icon: '⚖️' },
  { to: '/trading', label: 'کنترل معاملات (استاپ)', icon: '🛑' },
  { to: '/products', label: 'محصولات', icon: '🛍️' },
  { to: '/markups', label: 'درصد سود (طلا و نقره)', icon: '💰' },
  { to: '/pricing', label: 'قیمت خام', icon: '📈' },
  { to: '/orders', label: 'سفارش‌ها', icon: '🧾' },
  { to: '/users', label: 'کاربران', icon: '👥' },
  { to: '/reports', label: 'گزارش فروش', icon: '📋' }
]

const Layout: React.FC = () => {
  const { user, logout } = useAuth()
  const navigate = useNavigate()
  const location = useLocation()
  const [trading, setTrading] = useState<TradingStatus | null>(null)
  const [mobileOpen, setMobileOpen] = useState(false)

  const doLogout = async () => {
    await logout()
    navigate('/login')
  }

  // وضعیت استاپ معاملات را دوره‌ای می‌خوانیم تا بنر هشدار به‌روز بماند
  useEffect(() => {
    let alive = true
    const load = () =>
      tradingApi
        .getStatus()
        .then((res) => {
          if (alive && res.data.success && res.data.data) setTrading(res.data.data)
        })
        .catch(() => {})

    load()
    const timer = setInterval(load, 20000)
    return () => {
      alive = false
      clearInterval(timer)
    }
  }, [location.pathname])

  // با هر جابه‌جایی صفحه، منوی موبایل بسته شود
  useEffect(() => {
    setMobileOpen(false)
  }, [location.pathname])

  const sidebar = (
    <>
      <div className="p-5 border-b border-gray-800">
        <div className="flex items-center gap-2">
          <img src="/favicon.svg" alt="" className="w-9 h-9" />
          <div>
            <div className="font-black text-white leading-none">پنل مدیریت</div>
            <div className="text-[11px] text-gray-400 mt-1">طلا و نقره</div>
          </div>
        </div>
      </div>

      <nav className="flex-1 p-3 space-y-1 overflow-y-auto">
        {MENU.map((item) => (
          <NavLink
            key={item.to + item.label}
            to={item.to}
            end={item.end}
            className={({ isActive }) =>
              `flex items-center gap-3 px-4 py-2.5 rounded-lg text-sm font-medium transition-colors ${
                isActive ? 'bg-primary-600 text-white' : 'text-gray-300 hover:bg-gray-800 hover:text-white'
              }`
            }
          >
            <span>{item.icon}</span>
            <span>{item.label}</span>
          </NavLink>
        ))}
      </nav>

      <div className="p-4 border-t border-gray-800">
        {user && (
          <div className="text-xs text-gray-400 mb-3">
            {user.firstName} {user.lastName}
            <div dir="ltr" className="text-[10px] truncate">{user.email}</div>
          </div>
        )}
        <a href={`${window.location.origin.replace(':5174', ':5173')}/`} target="_blank" rel="noreferrer" className="btn btn-secondary w-full !py-2 !text-xs mb-2">
          🛒 مشاهده فروشگاه
        </a>
        <button className="btn btn-danger w-full !py-2 !text-xs" onClick={doLogout}>
          خروج از پنل
        </button>
      </div>
    </>
  )

  return (
    <div className="min-h-screen">
      {/* سایدبار دسکتاپ */}
      <aside className="hidden lg:flex w-60 bg-gray-900 text-gray-200 flex-col fixed inset-y-0 right-0 z-30">
        {sidebar}
      </aside>

      {/* پوشش تیره پشت منوی موبایل */}
      {mobileOpen && (
        <div
          className="lg:hidden fixed inset-0 bg-black/50 z-40"
          onClick={() => setMobileOpen(false)}
          aria-hidden="true"
        />
      )}

      {/* سایدبار موبایل (کشویی از راست) */}
      <aside
        className={`lg:hidden w-72 max-w-[85vw] bg-gray-900 text-gray-200 flex flex-col fixed inset-y-0 right-0 z-50 transition-transform duration-300 ${
          mobileOpen ? 'translate-x-0' : 'translate-x-full'
        }`}
      >
        <button
          className="absolute top-4 left-3 text-gray-400 hover:text-white text-2xl leading-none p-1"
          onClick={() => setMobileOpen(false)}
          aria-label="بستن منو"
        >
          ×
        </button>
        {sidebar}
      </aside>

      {/* Main */}
      <div className="lg:mr-60 flex flex-col min-h-screen">
        {/* هدر موبایل */}
        <header className="lg:hidden sticky top-0 z-30 bg-gray-900 text-white flex items-center justify-between px-4 py-3 shadow-md">
          <div className="flex items-center gap-2">
            <img src="/favicon.svg" alt="" className="w-8 h-8" />
            <span className="font-black">پنل مدیریت</span>
          </div>
          <button
            className="p-2 rounded-lg hover:bg-gray-800"
            onClick={() => setMobileOpen(true)}
            aria-label="باز کردن منو"
          >
            <svg width="22" height="22" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M4 5 H16 M4 10 H16 M4 15 H16" />
            </svg>
          </button>
        </header>

        <main className="flex-1 p-4 sm:p-6 lg:p-8 overflow-x-hidden">
          {/* بنر هشدار استاپ معاملات */}
          {trading?.anyStopped && (
            <div className="mb-5 rounded-xl border-2 border-red-300 bg-red-50 px-4 sm:px-5 py-3 flex flex-wrap items-center justify-between gap-3">
              <div className="flex items-center gap-3">
                <span className="text-xl">⛔</span>
                <div className="text-sm">
                  <b className="text-red-800">توجه: بخشی از معاملات متوقف است.</b>
                  <div className="text-xs text-red-700 mt-0.5">
                    {trading.sellStopped && <span className="ml-3">معاملات فروش متوقف — قیمت‌ها برای مشتری ستاره‌دار است</span>}
                    {trading.buyStopped && <span className="ml-3">معاملات خرید متوقف — پنل فروش به ما بسته است</span>}
                  </div>
                </div>
              </div>
              <NavLink to="/trading" className="btn btn-danger !py-1.5 !text-xs">
                مدیریت استاپ
              </NavLink>
            </div>
          )}
          <Outlet />
        </main>
      </div>
    </div>
  )
}

export default Layout
